<template>
  <div v-if="isActive" class="static-tour-overlay">
    <!-- Backdrop -->
    <div class="tour-backdrop"></div>
    
    <!-- Tour Modal - Full Screen -->
    <div class="tour-modal">
      <!-- Close Button -->
      <button class="modal-close" @click="skipTour" aria-label="Close tour">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
        </svg>
      </button>

      <!-- Step Indicator -->
      <div class="step-indicator">
        <span class="step-number">Step {{ currentStepIndex + 1 }}</span>
        <span class="step-divider">/</span>
        <span class="step-total">{{ steps.length }}</span>
      </div>

      <!-- Content Layout: Two Columns for non-first steps -->
      <div class="tour-main-content" :class="{ 'text-only': currentStepIndex === 0 }">
        <!-- Left Side: Screenshot (hidden for first step) -->
        <div v-if="currentStepIndex > 0" class="screenshot-section">
          <div class="screenshot-container">
            <div class="screenshot-wrapper">
              <img 
                :src="currentStep.screenshot" 
                :alt="currentStep.title"
                class="screenshot-image"
              />
              <!-- Interactive Arrows and Indicators -->
              <div v-if="currentStep.indicators" class="screenshot-indicators">
                <!-- Arrow Indicators -->
                <div 
                  v-for="(indicator, index) in currentStep.indicators"
                  :key="'indicator-' + index"
                  class="indicator-element"
                  :class="indicator.type"
                  :style="indicator.style"
                >
                  <div v-if="indicator.type === 'arrow'" class="arrow-pointer">
                    <svg width="40" height="40" viewBox="0 0 40 40" :style="{ transform: `rotate(${indicator.rotation || 0}deg)` }">
                      <path d="M20 5 L35 20 L27 20 L27 35 L13 35 L13 20 L5 20 Z" fill="currentColor"/>
                    </svg>
                  </div>
                  <div v-if="indicator.type === 'circle'" class="circle-highlight">
                    <div class="pulse-ring"></div>
                  </div>
                  <div v-if="indicator.type === 'number'" class="number-badge">
                    {{ indicator.number }}
                  </div>
                  <span v-if="indicator.label" class="indicator-label">{{ indicator.label }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Side: Text Content (Full width for first step) -->
        <div class="content-section">
          <div class="tour-content">
            <!-- Welcome Screen for First Step -->
            <div v-if="currentStepIndex === 0" class="welcome-screen">
              <div class="welcome-icon">{{ currentStep.icon }}</div>
              <h1 class="welcome-title">{{ currentStep.title }}</h1>
              <p class="welcome-description">{{ currentStep.description }}</p>
              
              <div class="welcome-features">
                <div 
                  v-for="(point, index) in currentStep.keyPoints"
                  :key="index"
                  class="welcome-feature-card"
                >
                  <div class="feature-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                    </svg>
                  </div>
                  <span class="feature-text">{{ point }}</span>
                </div>
              </div>
            </div>

            <!-- Regular Step Content -->
            <div v-else>
              <h2 class="tour-title">
                <span class="title-icon">{{ currentStep.icon }}</span>
                {{ currentStep.title }}
              </h2>
              
              <p class="tour-description">{{ currentStep.description }}</p>

              <!-- Key Points -->
              <div v-if="currentStep.keyPoints" class="key-points">
                <div 
                  v-for="(point, index) in currentStep.keyPoints"
                  :key="index"
                  class="key-point-item"
                >
                  <div class="point-number">{{ index + 1 }}</div>
                  <span class="point-text">{{ point }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Progress Dots -->
          <div class="progress-section">
            <div class="progress-dots">
              <button
                v-for="(step, index) in steps"
                :key="index"
                class="progress-dot"
                :class="{ 
                  'dot-active': index === currentStepIndex,
                  'dot-completed': index < currentStepIndex
                }"
                @click="goToStep(index)"
                :aria-label="`Go to step ${index + 1}`"
              >
                <span v-if="index < currentStepIndex" class="dot-check">✓</span>
              </button>
            </div>
          </div>

          <!-- Navigation Buttons -->
          <div class="tour-actions">
            <button 
              v-if="currentStepIndex > 0"
              class="btn-tour btn-secondary"
              @click="previousStep"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
              </svg>
              Previous
            </button>
            
            <button 
              v-if="currentStepIndex < steps.length - 1"
              class="btn-tour btn-primary"
              @click="nextStep"
            >
              Next
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
              </svg>
            </button>
            
            <button 
              v-else
              class="btn-tour btn-finish"
              @click="finishTour"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
              Get Started!
            </button>
          </div>

          <!-- Skip Link -->
          <button class="tour-skip-link" @click="skipTour">
            Skip tour
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';

interface TourIndicator {
  type: 'arrow' | 'circle' | 'number';
  style: any;
  label?: string;
  rotation?: number;
  number?: number;
}

interface TourStep {
  title: string;
  description: string;
  screenshot?: string;
  icon?: string;
  keyPoints?: string[];
  indicators?: TourIndicator[];
}

@Component
export default class StaticTour extends Vue {
  @Prop({ default: true })
  autoStart!: boolean;

  @Prop({ default: 'proxymed_tour_completed' })
  storageKey!: string;

  private isActive: boolean = false;
  private currentStepIndex: number = 0;

  // Tour steps with indicators
  private steps: TourStep[] = [
    {
      title: 'Welcome to ProxyMed',
      description: 'Your personal trainer for clinical decision-making',
      icon: '👋',
      keyPoints: [
        'AI-powered clinical decision support',
        'Evidence-based recommendations',
        'Multiple expert opinions available'
      ]
      // No screenshot for welcome screen
    },
    {
      title: 'Select Your Medical Field',
   
      description: 'Start by choosing the relevant medical department or specialty. Free options include Interdisciplinary Emergency Department and Dentistry Emergency Department.',
      screenshot: require('@/assets/screenshots/1.png'),
      icon: '🏥',
      keyPoints: [
        'Choose from various medical specialties',
        'Free and Pro options available',
        'Each field has specialized digital twins'
      ],
      indicators: [
        {
          type: 'arrow',
          style: {
            top: '70%',
           
            color: '#F59E0B'
          },
          rotation: 360,
          label: 'Click on a department'
        },
        {
          type: 'circle',
          style: {
            top: '34%',
    /* left: 0%; */
    width: '163px',
    height: '123px'
          }
        }
      ]
    },
    {
      title: 'Choose a Leading Symptom',
      description: 'Select the primary symptom or condition you\'re assessing. This helps narrow down the questionnaire to the most relevant questions.',
      screenshot: require('@/assets/screenshots/2.png'),
      icon: '🩺',
      keyPoints: [
        'Browse symptoms by category',
        'Multiple symptoms available per department',
        'Navigation is always tracked in breadcrumbs'
      ],
      indicators: [
       
        {
          type: 'arrow',
          style: {
            top: '40%',
           
            color: '#10B981'
          },
          rotation: 360,
          label: 'Select a symptom'
        },
        {
          type: 'circle',
          style: {
            top: '15%',
    left: '1%',
    width: '155px',
    height: '130px'
          }
        }
      ]
    },
    {
      title: 'Select Your Digital Twin',
      description: 'Choose which expert digital twin will guide your clinical decision. Each expert is validated and specialized in their field.',
      screenshot: require('@/assets/screenshots/3.png'),
      icon: '👨‍⚕️',
      keyPoints: [
        'ProxyMed Board of Experts (comprehensive analysis)',
        'Specialized AWMF Guidelines (evidence-based)',
        'Additional expert options for comparison'
      ],
      indicators: [
        {
          type: 'arrow',
          style: {
            top: '40%',
            left: '50%',
            color: '#8B5CF6'
          },
          rotation: 180,
          label: 'Choose your expert'
        },
        
        
      ]
    },
    {
  title: 'Assessment Overview',
  description: 'View your selected path and begin the questionnaire. The breadcrumb shows your current assessment journey.',
  screenshot: require('@/assets/screenshots/4.png'),
  icon: '📋',
  keyPoints: [
    'Track your selected department and symptom',
    'Edit selections anytime',
    'Clear overview of assessment path'
  ],
  indicators: [
    {
      type: 'circle',
      style: {
        top: '39%',
        left: '43%',
        width: '100px',
        height: '60px'
      }
    },
    {
      type: 'arrow',
      style: {
        bottom: '30%',
        right: '45%',
        color: '#EF4444'
      },
      rotation: 360,
      label: 'Answer questionn  and Save'
    }
  ]
},
    {
      title: 'Answer Questions - Patient Status',
      description: 'Begin with patient status questions. Your progress is tracked at the top, showing: Patient Status → Diagnostic Status → Result.',
      screenshot: require('@/assets/screenshots/5.png'),
      icon: '❓',
      keyPoints: [
        'Answer questions step by step',
        'Responses are saved automatically',
        'Progress through multiple sections'
      ],
      indicators: [
        // {
        //   type: 'number',
        //   style: {
        //     top: '12%',
        //     left: '30%'
        //   },
        //   number: 1,
        //   label: 'Track progress'
        // },
        {
          type: 'arrow',
          style: {
            top: '80%',
            left: '39%',
            color: '#3B82F6'
          },
          rotation: 360,
          label: 'Answer question  and Save'
        }
      ]
    },
    {
      title: 'Detailed Results',
      description: 'Review the complete clinical recommendation including all key decision criteria and references used by the expert.',
      screenshot: require('@/assets/screenshots/10.png'),
      icon: '🎯',
      keyPoints: [
        'Detailed management recommendations',
        'Complete decision criteria breakdown',
        'Reference documentation included',
        'Start a new assessment anytime'
      ],
      indicators: [
        // {
        //   type: 'circle',
        //   style: {
        //     top: '30%',
        //     left: '20%',
        //     width: '60%',
        //     height: '40%'
        //   }
        // },
        // {
        //   type: 'arrow',
        //   style: {
        //     bottom: '20%',
        //     right: '25%',
        //     color: '#10B981'
        //   },
        //   rotation: 90,
        //   label: 'Start new assessment'
        // }
      ]
    }
  ];

  get currentStep(): TourStep {
    return this.steps[this.currentStepIndex];
  }

  mounted() {
    if (this.autoStart && !this.hasCompletedTour()) {
      setTimeout(() => {
        this.startTour();
      }, 500);
    }
  }

  beforeDestroy() {
    this.removeBodyOverflow();
  }

  private startTour() {
    this.isActive = true;
    this.currentStepIndex = 0;
    this.addBodyOverflow();
    this.$emit('tour-started');
  }

  private nextStep() {
    if (this.currentStepIndex < this.steps.length - 1) {
      this.currentStepIndex++;
      this.$emit('step-changed', this.currentStepIndex);
    }
  }

  private previousStep() {
    if (this.currentStepIndex > 0) {
      this.currentStepIndex--;
      this.$emit('step-changed', this.currentStepIndex);
    }
  }

  private goToStep(index: number) {
    if (index <= this.currentStepIndex || index < this.steps.length) {
      this.currentStepIndex = index;
      this.$emit('step-changed', this.currentStepIndex);
    }
  }

  private finishTour() {
    this.completeTour();
    this.$emit('tour-completed');
  }

  private skipTour() {
    this.completeTour();
    this.$emit('tour-skipped');
  }

  private completeTour() {
    this.isActive = false;
    this.currentStepIndex = 0;
    this.removeBodyOverflow();
    localStorage.setItem(this.storageKey, 'true');
  }

  private hasCompletedTour(): boolean {
    return localStorage.getItem(this.storageKey) === 'true';
  }

  public resetTour() {
    localStorage.removeItem(this.storageKey);
    this.startTour();
  }

  private addBodyOverflow() {
    document.body.style.overflow = 'hidden';
  }

  private removeBodyOverflow() {
    document.body.style.overflow = '';
  }
}
</script>

<style scoped>
.static-tour-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tour-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  backdrop-filter: blur(4px);
  animation: fadeIn 0.3s ease;
}

.tour-modal {
  position: relative;
  background: white;
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  animation: fadeIn 0.3s ease;
  overflow: hidden;
}

.modal-close {
  position: fixed;
  top: 24px;
  right: 24px;
  background: rgba(255, 255, 255, 0.95);
  border: none;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  z-index: 100;
  color: #6B7280;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.modal-close:hover {
  background: white;
  color: #374151;
  transform: scale(1.1);
}

.step-indicator {
  position: fixed;
  top: 24px;
  left: 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 12px 24px;
  border-radius: 24px;
  font-size: 16px;
  font-weight: 600;
  z-index: 100;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.step-number {
  font-weight: 700;
}

.step-divider {
  margin: 0 8px;
  opacity: 0.7;
}

.step-total {
  opacity: 0.9;
}

/* Main Content Layout */
.tour-main-content {
  display: flex;
  height: 100vh;
  overflow: hidden;
}

.tour-main-content.text-only {
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

/* Screenshot Section - Left Side */
.screenshot-section {
  flex: 1;
  background: #F9FAFB;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 40px;
  overflow: hidden;
}

.screenshot-container {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.screenshot-wrapper {
  position: relative;
  max-width: 100%;
  max-height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.screenshot-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
}

/* Interactive Indicators */
.screenshot-indicators {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
}

.indicator-element {
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  animation: indicatorAppear 0.5s ease-out;
}

.arrow-pointer {
  color: #F59E0B;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2));
  animation: bounce 2s ease-in-out infinite;
}

.circle-highlight {
  position: relative;
  width: 100%;
  height: 100%;
  border: 4px solid #F59E0B;
  border-radius: 12px;
  animation: pulse 2s ease-in-out infinite;
}

.pulse-ring {
  position: absolute;
  top: -4px;
  left: -4px;
  right: -4px;
  bottom: -4px;
  border: 4px solid #F59E0B;
  border-radius: 12px;
  animation: pulseRing 2s ease-out infinite;
}

.number-badge {
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 18px;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  animation: pulse 2s ease-in-out infinite;
}

.indicator-label {
  background: rgba(0, 0, 0, 0.85);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

/* Content Section - Right Side */
.content-section {
  flex: 0 0 500px;
  background: white;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.tour-main-content.text-only .content-section {
  flex: 0 0 800px;
  background: white;
  border-radius: 24px;
  margin: 40px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.tour-content {
  flex: 1;
  padding: 100px 48px 32px;
  overflow-y: auto;
}

/* Welcome Screen */
.welcome-screen {
  text-align: center;
  padding: 40px 0;
}

.welcome-icon {
  font-size: 80px;
  margin-bottom: 24px;
  animation: float 3s ease-in-out infinite;
}

.welcome-title {
  font-size: 42px;
  font-weight: 800;
  color: #1F2937;
  margin-bottom: 20px;
  line-height: 1.2;
}

.welcome-description {
  font-size: 18px;
  color: #6B7280;
  line-height: 1.6;
  margin-bottom: 48px;
}

.welcome-features {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 40px;
}

.welcome-feature-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: #F9FAFB;
  border-radius: 12px;
  border-left: 4px solid #667eea;
  text-align: left;
  transition: transform 0.2s;
}

.welcome-feature-card:hover {
  transform: translateX(8px);
}

.feature-icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.feature-text {
  font-size: 16px;
  color: #374151;
  font-weight: 500;
}

/* Regular Step Content */
.tour-title {
  font-size: 28px;
  font-weight: 700;
  color: #1F2937;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.title-icon {
  font-size: 36px;
}

.tour-description {
  font-size: 16px;
  color: #6B7280;
  line-height: 1.6;
  margin-bottom: 24px;
  white-space: pre-wrap;
}

.key-points {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 24px;
}

.key-point-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px;
  background: #F9FAFB;
  border-radius: 10px;
  border-left: 3px solid #667eea;
}

.point-number {
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
}

.point-text {
  font-size: 14px;
  color: #374151;
  line-height: 1.5;
  padding-top: 4px;
}

/* Progress Section */
.progress-section {
  padding: 24px 48px;
  border-top: 1px solid #E5E7EB;
  background: #F9FAFB;
}

.progress-dots {
  display: flex;
  gap: 10px;
  justify-content: center;
  align-items: center;
}

.progress-dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #D1D5DB;
  border: none;
  cursor: pointer;
  transition: all 0.3s;
  padding: 0;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.progress-dot:hover {
  background: #9CA3AF;
  transform: scale(1.3);
}

.progress-dot.dot-active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  width: 40px;
  border-radius: 7px;
}

.progress-dot.dot-completed {
  background: #10B981;
  width: 24px;
  height: 24px;
}

.dot-check {
  color: white;
  font-size: 14px;
  font-weight: 700;
}

/* Navigation Buttons */
.tour-actions {
  display: flex;
  gap: 12px;
  padding: 24px 48px;
  background: white;
  border-top: 1px solid #E5E7EB;
}

.btn-tour {
  flex: 1;
  padding: 16px 24px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 28px rgba(102, 126, 234, 0.4);
}

.btn-secondary {
  background: #F3F4F6;
  color: #6B7280;
}

.btn-secondary:hover {
  background: #E5E7EB;
  color: #374151;
}

.btn-finish {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
}

.btn-finish:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 28px rgba(16, 185, 129, 0.4);
}

.tour-skip-link {
  display: block;
  width: 100%;
  padding: 16px;
  background: transparent;
  border: none;
  color: #9CA3AF;
  font-size: 14px;
  cursor: pointer;
  text-decoration: underline;
  transition: color 0.2s;
}

.tour-skip-link:hover {
  color: #6B7280;
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes indicatorAppear {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.9;
  }
}

@keyframes pulseRing {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(1.3);
    opacity: 0;
  }
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-20px); }
}

/* Responsive */
@media (max-width: 1200px) {
  .content-section {
    flex: 0 0 450px;
  }
}

@media (max-width: 768px) {
  .tour-main-content {
    flex-direction: column;
  }

  .screenshot-section {
    flex: 1;
    padding: 80px 20px 20px;
  }

  .content-section {
    flex: 0 0 auto;
    max-height: 50vh;
  }

  .tour-content {
    padding: 24px;
  }

  .welcome-title {
    font-size: 32px;
  }

  .tour-title {
    font-size: 20px;
    white-space: pre-wrap
  }

  .tour-actions {
    padding: 20px 24px;
  }

  .progress-section {
    padding: 20px 24px;
  }

  .tour-main-content.text-only .content-section {
    flex: 1;
    margin: 20px;
  }
}

@media (max-width: 480px) {
  .step-indicator {
    top: 16px;
    left: 16px;
    padding: 8px 16px;
    font-size: 14px;
  }

  .modal-close {
    top: 16px;
    right: 16px;
    width: 40px;
    height: 40px;
  }

  .screenshot-section {
    padding: 70px 15px 15px;
  }

  .tour-content {
    padding: 20px;
  }

  .welcome-title {
    font-size: 28px;
  }

  .welcome-icon {
    font-size: 64px;
  }
}
</style>